/**
 * Gamepad focus navigation for menus / overlays (sidebar, Settings, Shop, etc.).
 */

import { GAMEPAD_MENU_STICK_REPEAT_MS } from "./gamepad-input";
import type { GamepadNavFrame } from "./gamepad-input";

const FOCUS_CLASS = "gp-focus";
const FOCUS_ATTR = "data-gp-focus";

const FOCUSABLE_SELECTOR = [
    "button:not([disabled]):not([aria-hidden='true'])",
    "a[href]:not([aria-hidden='true'])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[role='button']:not([aria-disabled='true'])",
    "[tabindex]:not([tabindex='-1'])",
    ".toggleSwitch",
    ".modeBox.settingsCard",
    ".gameHubCard",
    ".modeCard",
    ".shopCard",
    ".inventoryCard",
].join(",");

const IGNORE_CLOSEST = [
    ".hidden",
    "[hidden]",
    ".gp-nav-ignore",
    "#touchSteerLayer",
    "#touchFloatJoystick",
    "#hookAimJoystick",
    "#touchPlaySteerPad",
    "#orbitSteerLayer",
    ".touch-steer-layer",
].join(",");

let focused: HTMLElement | null = null;
let lastMoveAxis = "";
let lastMoveAt = 0;
let sliderHoldDir = 0;
let sliderHoldAcc = 0;

function isVisible(el: HTMLElement): boolean {
    if (el.classList.contains("hidden") || el.hasAttribute("hidden")) return false;
    if (el.closest(".hidden, [hidden]")) return false;
    const st = getComputedStyle(el);
    if (st.display === "none" || st.visibility === "hidden" || st.opacity === "0") return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
}

function isFocusCandidate(el: HTMLElement): boolean {
    if (!isVisible(el)) return false;
    if (el.closest(IGNORE_CLOSEST) && !el.classList.contains("settingsCard")) {
        // settings cards inside visible panels are ok; ignore only if an ancestor matches ignore list
        const bad = el.closest("#touchSteerLayer, #touchFloatJoystick, #hookAimJoystick, #touchPlaySteerPad, #orbitSteerLayer, .gp-nav-ignore");
        if (bad) return false;
    }
    if (el.getAttribute("aria-hidden") === "true") return false;
    // Prefer interactive controls over large card wrappers when both match
    return true;
}

function rootScreens(): HTMLElement[] {
    const roots: HTMLElement[] = [];
    const pause = document.getElementById("pauseScreen");
    if (pause && !pause.classList.contains("hidden")) roots.push(pause);

    const over = document.getElementById("gameOverScreen");
    if (over && !over.classList.contains("hidden")) roots.push(over);

    document.querySelectorAll<HTMLElement>(".screen").forEach((s) => {
        if (!s.classList.contains("hidden")) roots.push(s);
    });

    const side = document.getElementById("sideNav");
    if (side && side.classList.contains("open")) roots.push(side);

    const ctx = document.getElementById("modeContextMenu");
    if (ctx && !ctx.classList.contains("hidden")) roots.push(ctx);

    return roots;
}

function collectFocusables(): HTMLElement[] {
    const roots = rootScreens();
    if (!roots.length) return [];
    const seen = new Set<HTMLElement>();
    const out: HTMLElement[] = [];
    for (const root of roots) {
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach((el) => {
            if (seen.has(el)) return;
            if (!isFocusCandidate(el)) return;
            // Skip nested buttons inside an already-listed card if card itself is focusable —
            // actually prefer the button. Skip inert labels.
            if (el.tagName === "LABEL") return;
            seen.add(el);
            out.push(el);
        });
    }
    // Stable reading order: top-to-bottom, left-to-right
    out.sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        const dy = ra.top - rb.top;
        if (Math.abs(dy) > 12) return dy;
        return ra.left - rb.left;
    });
    return out;
}

function clearFocusVisual() {
    document.querySelectorAll(`.${FOCUS_CLASS}`).forEach((el) => {
        el.classList.remove(FOCUS_CLASS);
        el.removeAttribute(FOCUS_ATTR);
    });
}

function setFocused(el: HTMLElement | null) {
    clearFocusVisual();
    focused = el;
    if (!el) return;
    el.classList.add(FOCUS_CLASS);
    el.setAttribute(FOCUS_ATTR, "1");
    try {
        el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    } catch {
        /* ignore */
    }
}

function ensureFocus(): HTMLElement | null {
    const list = collectFocusables();
    if (!list.length) {
        setFocused(null);
        return null;
    }
    if (focused && list.includes(focused) && isFocusCandidate(focused)) return focused;
    setFocused(list[0]);
    return focused;
}

function moveFocus(dx: number, dy: number) {
    const list = collectFocusables();
    if (!list.length) {
        setFocused(null);
        return;
    }
    let idx = focused ? list.indexOf(focused) : -1;
    if (idx < 0) {
        setFocused(list[0]);
        return;
    }
    if (dy !== 0) {
        // Prefer spatial neighbor in vertical direction
        const cur = list[idx].getBoundingClientRect();
        const cy = cur.top + cur.height / 2;
        const cx = cur.left + cur.width / 2;
        let best: HTMLElement | null = null;
        let bestScore = Infinity;
        for (const el of list) {
            if (el === list[idx]) continue;
            const r = el.getBoundingClientRect();
            const ey = r.top + r.height / 2;
            const ex = r.left + r.width / 2;
            const vdy = ey - cy;
            if (dy > 0 && vdy < 8) continue;
            if (dy < 0 && vdy > -8) continue;
            const score = Math.abs(vdy) * 1.2 + Math.abs(ex - cx) * 0.35;
            if (score < bestScore) {
                bestScore = score;
                best = el;
            }
        }
        if (best) {
            setFocused(best);
            return;
        }
        idx = (idx + (dy > 0 ? 1 : -1) + list.length) % list.length;
        setFocused(list[idx]);
        return;
    }
    if (dx !== 0) {
        const cur = list[idx].getBoundingClientRect();
        const cy = cur.top + cur.height / 2;
        const cx = cur.left + cur.width / 2;
        let best: HTMLElement | null = null;
        let bestScore = Infinity;
        for (const el of list) {
            if (el === list[idx]) continue;
            const r = el.getBoundingClientRect();
            const ey = r.top + r.height / 2;
            const ex = r.left + r.width / 2;
            const vdx = ex - cx;
            if (dx > 0 && vdx < 8) continue;
            if (dx < 0 && vdx > -8) continue;
            if (Math.abs(ey - cy) > Math.max(cur.height, r.height) * 0.85 + 24) continue;
            const score = Math.abs(vdx) * 1.2 + Math.abs(ey - cy) * 0.35;
            if (score < bestScore) {
                bestScore = score;
                best = el;
            }
        }
        if (best) {
            setFocused(best);
            return;
        }
        idx = (idx + (dx > 0 ? 1 : -1) + list.length) % list.length;
        setFocused(list[idx]);
    }
}

function adjustRange(input: HTMLInputElement, dir: number) {
    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const step = Number(input.step || 1) || 1;
    let v = Number(input.value);
    if (!Number.isFinite(v)) v = min;
    v = Math.max(min, Math.min(max, v + dir * step));
    input.value = String(v);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
}

function activateFocused() {
    const el = ensureFocus();
    if (!el) return;

    if (el.classList.contains("toggleSwitch")) {
        el.click();
        return;
    }
    if (el instanceof HTMLInputElement && el.type === "range") {
        // Confirm does nothing special on sliders — L/R adjusts
        return;
    }
    if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
        el.click();
        return;
    }
    if (el instanceof HTMLSelectElement) {
        el.focus();
        return;
    }
    if (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && el.type === "text")) {
        el.focus();
        return;
    }
    // Mode cards: activate the card (start/select mode), never the "..." options button.
    if (el.classList.contains("modeBox") && el.hasAttribute("data-mode-id")) {
        el.click();
        return;
    }
    if (el.classList.contains("modeBoxMenuBtn")) {
        el.click();
        return;
    }
    // Settings card: activate first toggle/button/range inside (skip mode-options "...")
    if (el.classList.contains("settingsCard") || el.classList.contains("modeBox")) {
        const inner =
            el.querySelector<HTMLElement>(
                ".toggleSwitch, button.btn:not(.modeBoxMenuBtn), input[type='range'], button:not(.modeBoxMenuBtn)",
            ) ?? null;
        if (inner && inner !== el) {
            if (inner instanceof HTMLInputElement && inner.type === "range") {
                setFocused(inner);
                return;
            }
            inner.click();
            return;
        }
    }
    el.click();
}

function handleBack() {
    // Close mode options / description first if open
    const modeMenu = document.getElementById("modeContextMenu");
    if (modeMenu && !modeMenu.classList.contains("hidden")) {
        document.getElementById("modeCtxCloseBtn")?.click();
        return;
    }
    const detail = document.getElementById("modeDetailModal");
    if (detail && !detail.classList.contains("hidden")) {
        document.getElementById("modeDetailCloseBtn")?.click();
        return;
    }
    // Prefer visible Return button / close controls
    const returnBtn = document.getElementById("returnBtn");
    if (returnBtn && !returnBtn.classList.contains("hidden") && isVisible(returnBtn)) {
        returnBtn.click();
        return;
    }
    const closeBtns = document.querySelectorAll<HTMLElement>(
        "#modeCtxCloseBtn, .modeDetailModalClose, .modalCornerCloseBtn, [data-gp-back], .screen .btn[id$='ReturnBtn'], .screen .btn[id$='BackBtn']",
    );
    for (const b of closeBtns) {
        if (isVisible(b)) {
            b.click();
            return;
        }
    }
    // Keyboard-style: dispatch Escape for listeners that handle it
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
}

function tryAdjustSlider(dx: number, dt: number): boolean {
    const el = focused;
    if (!el) return false;
    let range: HTMLInputElement | null = null;
    if (el instanceof HTMLInputElement && el.type === "range") range = el;
    else range = el.querySelector("input[type='range']");
    if (!range) return false;
    if (dx === 0) {
        sliderHoldDir = 0;
        sliderHoldAcc = 0;
        return false;
    }
    if (sliderHoldDir !== dx) {
        sliderHoldDir = dx;
        sliderHoldAcc = 0;
        adjustRange(range, dx);
        return true;
    }
    sliderHoldAcc += dt;
    if (sliderHoldAcc >= 0.085) {
        sliderHoldAcc = 0;
        adjustRange(range, dx);
    }
    return true;
}

export function clearGamepadMenuFocus() {
    setFocused(null);
    lastMoveAxis = "";
    lastMoveAt = 0;
    sliderHoldDir = 0;
    sliderHoldAcc = 0;
}

export function pollGamepadMenuNav(frame: GamepadNavFrame) {
    document.body.classList.add("gp-menu-nav");
    ensureFocus();

    if (frame.confirm) activateFocused();
    if (frame.back) handleBack();

    const dx = frame.moveX;
    const dy = frame.moveY;

    if (dx !== 0 && tryAdjustSlider(dx, frame.dt)) {
        // Horizontal on slider = value change, not focus move
        return;
    }

    if (dx === 0 && dy === 0) {
        lastMoveAxis = "";
        sliderHoldDir = 0;
        sliderHoldAcc = 0;
        return;
    }

    const axis = `${dx},${dy}`;
    const t = performance.now();
    if (axis === lastMoveAxis && t - lastMoveAt < GAMEPAD_MENU_STICK_REPEAT_MS) return;
    lastMoveAxis = axis;
    lastMoveAt = t;
    moveFocus(dx, dy);
}

export function endGamepadMenuNavSession() {
    document.body.classList.remove("gp-menu-nav");
    clearGamepadMenuFocus();
}
